import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCheck, ChevronRight, MoreVertical } from 'lucide-react';
import NextGenEntityTree, {
  generateEmptySkeleton,
  mergeWithSkeleton,
} from 'src/features/messages/components/feeds/reading-pane/next-gen/NextGenEntityTree';
import { AccordionContent, AccordionItem, AccordionTrigger } from 'src/shared/components/ui/accordion';
import SubtleCapsHeaders from 'src/shared/components/typography/SubtleCapsHeaders';
import { Loading } from 'src/shared/components/ui/loaders/loading';
import { cn } from 'src/shared/lib/utils';
import { setEntityValue } from 'src/shared/lib/entities.utils';
import WithAccessControl from 'src/shared/components/utils/WithAccessControl';
import WithTooltip from 'src/shared/components/utils/WithTooltip';
import TripleDotActions from 'src/shared/components/ui/dropdowns/triple-dot-actions';
import { Entities } from 'src/features/messages/types/message.types';
import { ExtractionModelListItem } from 'src/shared/types/extraction-model.types';
import { useExtractionModel, useInvokeExtractionModel, useCreateTestItem } from 'src/features/models/queries/next-gen';
import { validatePrediction } from 'src/shared/lib/next-gen/validation.utils';
import { useFeatureFlag } from 'src/shared/queries/utils/useFeatureFlag';
import { useToast } from 'src/shared/components/ui/use-toast';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { Role } from 'src/shared/types/organisation.types';

type NextGenEntityModelPanelProps = {
  modelId: string;
  modelListItem: ExtractionModelListItem;
  modelEntities: Entities[string];
  integrationId: string;
  threadId: string;
  isFullyVerified: boolean;
  isSaving: boolean;
  onEntityChange?: (key: string, value: Entities[string] | Entities[string][string], path: string[]) => void;
  onPredictionComplete?: (prediction: Record<string, unknown>) => void;
  onMarkAllAsCorrect: () => void;
};

export default function NextGenEntityModelPanel({
  modelId,
  modelListItem,
  modelEntities,
  integrationId,
  threadId,
  isFullyVerified,
  isSaving,
  onEntityChange,
  onPredictionComplete,
  onMarkAllAsCorrect,
}: NextGenEntityModelPanelProps) {
  const { toast } = useToast();
  const [menuOpen, setMenuOpen] = useState(false);

  const { role } = useOrg();

  // Fetch the full model with entities
  const { data: fullModel, isLoading: isLoadingModel } = useExtractionModel(modelId);

  // Invoke extraction model mutation
  const invokeModel = useInvokeExtractionModel();

  // Create test item mutation
  const createTestItem = useCreateTestItem();

  // Check feature flag
  const { data: hasTestSetsFeature } = useFeatureFlag('EXPERIMENTAL_ENTITY_TEST_SETS');

  // Validate prediction against JSON schema
  // Generate merged entities with skeleton (for RAW view and test items)
  const mergedEntities = useMemo(() => {
    if (!fullModel) return modelEntities;
    const skeleton = generateEmptySkeleton(fullModel);
    return mergeWithSkeleton(skeleton, modelEntities);
  }, [fullModel, modelEntities]);

  const validationResult = useMemo(() => {
    if (!fullModel?.jsonSchema || !modelEntities || Object.keys(modelEntities).length === 0) {
      return { isValid: true, error: null };
    }
    return validatePrediction(modelEntities, fullModel.jsonSchema);
  }, [fullModel?.jsonSchema, modelEntities]);

  const isPredictionInvalid = !validationResult.isValid;

  async function handlePredict() {
    // Keep menu open while predicting
    setMenuOpen(true);
    try {
      const result = await invokeModel.mutateAsync({
        integrationId,
        threadId,
        modelId,
      });

      // Call the callback with the prediction result
      onPredictionComplete?.(result.prediction);
    } catch (error) {
      console.error('Failed to invoke extraction model:', error);
    } finally {
      // Close menu after prediction completes
      setMenuOpen(false);
    }
  }

  function handleCopyRaw() {
    navigator.clipboard.writeText(JSON.stringify(mergedEntities, null, 4));
    toast({
      title: 'Copied to clipboard',
      variant: 'success',
    });
  }

  async function handleAddToTestSet() {
    try {
      await createTestItem.mutateAsync({
        integrationId,
        threadId,
        modelId,
        correctPrediction: mergedEntities as Record<string, unknown>,
      });
      toast({
        title: 'Added to test set',
        variant: 'success',
      });
    } catch (error) {
      console.error('Failed to add to test set:', error);
      toast({
        title: 'Failed to add to test set',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'warning',
      });
    }
  }

  return (
    <AccordionItem value={modelId} className="border-none">
      <div className="flex items-center gap-1">
        <AccordionTrigger headless className="flex w-full items-center gap-1 group h-6">
          <ChevronRight
            size={16}
            className="text-typography-grey transition-transform duration-200 group-data-[state=open]:rotate-90"
          />
          <SubtleCapsHeaders>{modelListItem.name || modelId}</SubtleCapsHeaders>
          {isLoadingModel && <Loading size={10} className="text-typography-grey" />}
          {isFullyVerified && (
            <WithTooltip tooltip="This output has been fully verified">
              <CheckCheck size={16} className="text-success" />
            </WithTooltip>
          )}
          {isSaving && (
            <div className="pl-2 flex items-center gap-1">
              <Loading size={10} className="text-typography-grey" />
              <span className="text-xs text-typography-">Saving...</span>
            </div>
          )}
        </AccordionTrigger>

        <WithAccessControl allowedRoles={['ADMIN', 'CONTRIBUTOR', 'SUPPORT']}>
          <div className="flex items-center ml-auto gap-2 pr-2">
            {/* Kebab Menu */}
            <TripleDotActions
              open={menuOpen}
              onOpenChange={setMenuOpen}
              extra={[
                ...((['SUPPORT', 'ADMIN', 'CONTRIBUTOR'] as Role[]).includes(role)
                  ? [
                      {
                        text: 'Rerun model',
                        icon: 'Sparkles' as const,
                        loading: invokeModel.isPending,
                        onClick: () => {
                          handlePredict();
                        },
                      },
                    ]
                  : []),
                ...(hasTestSetsFeature && fullModel
                  ? [
                      {
                        text: 'Add to test set',
                        icon: 'FlaskConical' as const,
                        onClick: () => {
                          handleAddToTestSet();
                        },
                      },
                    ]
                  : []),
                {
                  text: 'Copy raw output',
                  icon: 'Copy',
                  onClick: () => {
                    handleCopyRaw();
                  },
                },
              ]}
            >
              <button
                onClick={(e) => e.stopPropagation()}
                className="text-typography-grey hover:text-typography-black transition-all h-4"
              >
                <MoreVertical size={16} />
              </button>
            </TripleDotActions>
          </div>
        </WithAccessControl>
      </div>

      <AccordionContent className="pt-1">
        {isLoadingModel ? (
          <div className="flex items-center justify-center py-4">
            <Loading size={20} />
          </div>
        ) : fullModel ? (
          <div className="flex flex-col gap-1">
            <div className="flex flex-col gap-1">
              {isPredictionInvalid && (
                <div className="flex items-start gap-2 p-3 bg-warning-50 border border-warning-200 rounded-sm">
                  <AlertTriangle size={16} className="text-warning-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-warning-900">
                      Entities from an older version of the extraction model can no longer be validated.
                    </p>
                  </div>
                </div>
              )}
              <NextGenEntityTree
                model={fullModel}
                values={mergedEntities}
                editable={!isPredictionInvalid && !!onEntityChange}
                onSave={(key, value, path) => {
                  // Apply change to merged skeleton to ensure full structure is preserved
                  const updatedEntities = setEntityValue(mergedEntities, path, value as Entities[string][string]);
                  // Pass the full updated entities as the value so the parent gets complete structure
                  onEntityChange?.(key, updatedEntities, []);
                }}
              />
              {!isPredictionInvalid && (
                <button
                  onClick={onMarkAllAsCorrect}
                  disabled={isSaving || isFullyVerified}
                  className={cn(`text-sm select-none h-[30px] shrink-0 w-full transition-all text-center border text-success-600 
                                        hover:bg-success-600 hover:text-white hover:border-success-600 bg-success-500/20 rounded-sm flex-center gap-2 px-2 py-1
                                        border-success-600/20 disabled:pointer-events-none mt-1`)}
                >
                  {isSaving ? <Loading size={14} color="#6B7280" /> : <CheckCheck size={16} />}
                  {isFullyVerified ? 'Fully Verified' : 'Mark all as correct'}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500 italic p-2">Failed to load model</div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
